import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Button,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
  Tag,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const drawerStyles = {
  header: { padding: "16px 18px" },
  body: { padding: "14px 18px 18px" },
  footer: { padding: "12px 18px" },
};

const SELL_MODE_OPTIONS = [
  { label: "Per Room", value: "per_room" },
  { label: "Per Person", value: "per_person" },
];

const RATE_MODE_OPTIONS = [
  { label: "Manual", value: "manual" },
  { label: "Derived", value: "derived" },
];

export default function RatePlanDrawer({
  open,
  mode = "create",
  propertyId,
  rooms = [],
  currency = "INR",
  initialData = null,
  api,
  onClose,
  onSaved,
}) {
  const [form] = Form.useForm();
  const isEdit = mode === "edit";

  const roomOptions = useMemo(
    () =>
      (rooms || []).map((r) => ({
        value: r.pms_room_type_id,
        label: r.name,
      })),
    [rooms]
  );

  const sellMode = Form.useWatch("sell_mode", form);
  const maxPersons = Form.useWatch("max_persons", form);

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      form.setFieldsValue({
        title: initialData.title || "",
        pms_room_type_id: initialData.pms_room_type_id || undefined,
        currency: initialData.currency || currency,
        sell_mode: initialData.sell_mode || "per_room",
        rate_mode: initialData.rate_mode || "manual",
        max_persons: initialData.max_persons || 2,
        occ: initialData.occ || 2,
        supported_occupancies:
          initialData.supported_occupancies?.length
            ? initialData.supported_occupancies
            : initialData.occ
              ? [initialData.occ]
              : [2],
        is_active: initialData.is_active !== false,
      });
    } else {
      form.setFieldsValue({
        title: "",
        pms_room_type_id: undefined,
        currency,
        sell_mode: "per_room",
        rate_mode: "manual",
        max_persons: 2,
        occ: 2,
        supported_occupancies: [2],
        is_active: true,
      });
    }
  }, [open, initialData, currency, form]);

  useEffect(() => {
    if (!open) return;

    const mp = Math.max(1, Number(maxPersons || 1));

    if (sellMode === "per_room") {
      form.setFieldsValue({
        occ: mp,
        supported_occupancies: [mp],
      });
      return;
    }

    const current = form.getFieldValue("supported_occupancies") || [];
    const next = current.filter((x) => Number(x) <= mp);
    form.setFieldsValue({
      supported_occupancies: next.length ? next : Array.from({ length: mp }, (_, i) => i + 1),
    });
  }, [sellMode, maxPersons, open, form]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      const payload = {
        property_id: propertyId,
        pms_room_type_id: Number(values.pms_room_type_id),
        title: String(values.title || "").trim(),
        currency: values.currency || currency,
        sell_mode: values.sell_mode || "per_room",
        rate_mode: values.rate_mode || "manual",
        max_persons: Number(values.max_persons || 2),
        occ: values.sell_mode === "per_room" ? Number(values.occ || values.max_persons || 2) : null,
        supported_occupancies:
          values.sell_mode === "per_person"
            ? (values.supported_occupancies || []).map(Number).sort((a, b) => a - b)
            : [Number(values.occ || values.max_persons || 2)],
        is_active: Boolean(values.is_active),
      };

      if (isEdit) {
        if (!initialData?.id) {
          message.error("Missing rate plan id for edit.");
          return;
        }
        await api.updateRatePlan(initialData.id, payload);
        message.success("Rate plan updated successfully.");
      } else {
        await api.createRatePlan(payload);
        message.success("Rate plan created successfully.");
      }

      onSaved?.();
    } catch (e) {
      console.error(e);
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || e.message || "Rate plan save failed");
    }
  }

  const occupancyOptions = Array.from(
    { length: Math.max(1, Number(maxPersons || 1)) },
    (_, i) => ({
      value: i + 1,
      label: `Occ ${i + 1}`,
    })
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      zIndex={12050}
      styles={drawerStyles}
      destroyOnClose={false}
      maskClosable={false}
      title={
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(37,99,235,0.10)",
              border: "1px solid rgba(15,23,42,0.08)",
              fontWeight: 900,
              color: "#2563eb",
            }}
          >
            RP
          </div>

          <div style={{ flex: 1 }}>
            <Title level={4} style={{ margin: 0 }}>
              {isEdit ? "Edit Rate Plan" : "Create Rate Plan"}
            </Title>
            <Text type="secondary">
              {isEdit
                ? "Update Channex-linked rate plan settings safely."
                : "Create a new Channex-linked rate plan for the selected room type."}
            </Text>

            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Tag color="blue" style={{ marginInlineStart: 0 }}>
                {currency}
              </Tag>
              <Tag style={{ marginInlineStart: 0 }}>
                {isEdit ? "Edit mode" : "Create mode"}
              </Tag>
            </div>
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <Text type="secondary" style={{ fontWeight: 600 }}>
            Property: {propertyId || "—"}
          </Text>

          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit}>
              {isEdit ? "Save Changes" : "Create Rate Plan"}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <div
          style={{
            border: "1px solid rgba(15,23,42,0.08)",
            background: "rgba(15,23,42,0.015)",
            borderRadius: 16,
            padding: 14,
            marginBottom: 16,
          }}
        >
          <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
            Basic Details
          </Title>
          <Text type="secondary">These settings define the rate plan identity and room mapping.</Text>

          <Row gutter={14} style={{ marginTop: 14 }}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Rate Plan Title"
                rules={[{ required: true, message: "Please enter a rate plan title." }]}
              >
                <Input placeholder="Example: Best Available Rate - Deluxe Room" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={14}>
            <Col span={12}>
              <Form.Item
                name="pms_room_type_id"
                label="Room Type"
                rules={[{ required: true, message: "Please select a room type." }]}
              >
                <Select
                  placeholder="Select room type"
                  options={roomOptions}
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="currency" label="Currency" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div
          style={{
            border: "1px solid rgba(15,23,42,0.08)",
            borderRadius: 16,
            padding: 14,
            marginBottom: 16,
          }}
        >
          <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
            Pricing Model
          </Title>
          <Text type="secondary">
            Choose how the plan should behave in Channex and in your ARI grid.
          </Text>

          <Row gutter={14} style={{ marginTop: 14 }}>
            <Col span={12}>
              <Form.Item name="rate_mode" label="Rate Mode" rules={[{ required: true }]}>
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  options={RATE_MODE_OPTIONS}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="sell_mode" label="Sell Mode" rules={[{ required: true }]}>
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  options={SELL_MODE_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={14}>
            <Col span={12}>
              <Form.Item
                name="max_persons"
                label="Max Persons"
                rules={[{ required: true, message: "Please enter max persons." }]}
              >
                <InputNumber min={1} max={12} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div
          style={{
            border: "1px solid rgba(15,23,42,0.08)",
            borderRadius: 16,
            padding: 14,
          }}
        >
          <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
            Occupancy Setup
          </Title>
          <Text type="secondary">
            Make occupancy explicit so grid values and local database mapping stay aligned.
          </Text>

          <Divider style={{ margin: "14px 0" }} />

          {sellMode === "per_room" ? (
            <Row gutter={14}>
              <Col span={12}>
                <Form.Item
                  name="occ"
                  label="Selling Occupancy"
                  rules={[{ required: true, message: "Please select selling occupancy." }]}
                >
                  <Select options={occupancyOptions} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={14}>
              <Col span={24}>
                <Form.Item
                  name="supported_occupancies"
                  label="Supported Occupancies"
                  rules={[{ required: true, message: "Please select at least one occupancy." }]}
                >
                  <Select
                    mode="multiple"
                    options={occupancyOptions}
                    placeholder="Select occupancies"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Alert
            type="info"
            showIcon
            message={sellMode === "per_room" ? "Per Room selected" : "Per Person selected"}
            description={
              sellMode === "per_room"
                ? "Use one main selling occupancy for this plan. This is the safest model for most hotels."
                : "Use explicit supported occupancies when prices differ by guest count."
            }
          />
        </div>
      </Form>
    </Drawer>
  );
}